import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataGrid, {
  Button as DgButton,
  Column,
  Editing,
  Lookup,
  Paging,
  RequiredRule,
  Sorting,
} from "devextreme-react/data-grid";
import PAGE from "@config/page.config";
import { useSelector, useDispatch } from "react-redux";
import { useRouter } from "next/router";
import ExternalDocsEditor from "./ExternalDocsEditor";
import {
  setSelectedDoc,
  useCloneMasterDocMutation,
  useGetMasterDocQuery,
  useUpdateMasterDocsMutation,
} from "@slices/masterDocSlice";
import { Button, Form, Input, Label, Modal } from "@customcomponents";
import { swal, toast } from "@customcomponents/swal/instance";
import { Tooltip } from "devextreme-react/tooltip";
import SelectBox from "devextreme-react/select-box";
import { boolEditorRender } from "@common/renderFunctions";
import { setSelectedSnapshot } from "@slices/snapshotSlice";
import api from "@slices/api";

const docType = [
  { type_id: 1, type_nm: "Brand/Core/Sports" },
  { type_id: 2, type_nm: "Original Series & Movies" },
];

const statuses = [
  { status_cd: 1, status_nm: "Planning" },
  { status_cd: 2, status_nm: "Live" },
  { status_cd: 3, status_nm: "Ended" },
  { status_cd: 4, status_nm: "N/A" },
];

const externalDocsRender = (e) => {
  const data = e.value || [];
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div>
        {data.map((o, index) => (
          <a key={index} style={{ display: "block" }} href={o.url} target="_blank" rel="noopener noreferrer">
            {" "}
            {/* TODO: Don't use index as key */}
            {o.name}
          </a>
        ))}
      </div>
      <div>
        <i style={{ color: "#337ab7" }} className="dx-icon-edit" id={`edit-${e.key}`}></i>
        <Tooltip target={`#edit-${e.key}`} showEvent="mouseenter" hideEvent="mouseleave">
          Double click to edit
        </Tooltip>
      </div>
    </div>
  );
};

const MasterDocs = () => {
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneInitiativeCode, setCloneInitiativeCode] = useState(null);
  const [documentToClone, setDocumentToClone] = useState(null);
  const [initiatives, setInitiatives] = useState([]);

  // const data = useSelector((state) => state.masterDocReducer.data);
  const { data } = useGetMasterDocQuery();
  const dispatch = useDispatch();
  const [updateMasterDocs, { error: updateError }] = useUpdateMasterDocsMutation();
  const [cloneMasterDoc, { error: cloneError }] = useCloneMasterDocMutation();

  const router = useRouter();

  const user = useSelector((state) => state.user);
  const isAdmin = user.adminPermission ?? false;
  const isUser = user.editorPermission && user.viewerPermission && !isAdmin;
  const isExternal = user.externalPermission ?? false;
  const isAllowedRole = isAdmin || isUser;

  useEffect(() => {
    dispatch(setSelectedSnapshot(null));
    dispatch(api.util.resetApiState());
    dispatch(setSelectedDoc(null));
  }, []);

  useEffect(() => {
    // Check if data and data.initiatives are defined, else set to empty array
    setInitiatives(data && data.initiatives ? data.initiatives : []);
  }, [data]);

  const onInitNewRow = useCallback(
    (e) => {
      e.data.approved_ind = false;
      e.data.status_cd = 1;
      e.data.create_dt_ut = new Date();
      e.data.created_by_user_nm = user.name;

      // const cellElt = e.component.getCellElement(0, "approved_ind");
      // cellElt?.classList.remove("dx-cell-modified");
    },
    [user],
  );

  const onSaving = useCallback(async (e) => {
    e.cancel = true;

    if (e.changes.length) {
      try {
        e.component.cancelEditData();
        const response = await updateMasterDocs(e.changes);
        console.log("updateMasterDocs", response);

        if (response.error) {
          console.error("error occurred while saving");
          return;
        }

        // await e.component.refresh(true);
      } catch (error) {
        console.error("save error", error);
      }
    }
  }, []);

  const cloneIconClick = (e) => {
    console.log("clone", e);
    setDocumentToClone(e.row.data);
    setIsCloneModalOpen(true);
  };

  const onCloneNameChange = (e) => {
    setCloneName(e.target.value);
  };
  const onCloneInitiativeChange = (e) => {
    setCloneInitiativeCode(e.value);
  };

  const openMasterDocument = useCallback(({ row: { data } }) => {
    console.log("openMasterDocument", data);

    if (!data.master_doc_id) {
      return;
    }

    dispatch(
      setSelectedDoc({
        id: data.master_doc_id,
        name: data.master_doc_nm,
      }),
    );

    const formattedDocName = data.master_doc_nm.replace(/[#;/?:@&=+$,~!%^*()_`<> ]/g, "-");
    router.push(
      `/doc/${data.master_doc_id}/${formattedDocName}/summary`, // TODO: Make this work without /beats
    );
  }, []);

  const handleCloneSubmit = async (e) => {
    e.preventDefault();

    try {
      await cloneMasterDoc({
        masterDocId: documentToClone.master_doc_id,
        newDocData: { name: cloneName, initiative_id: cloneInitiativeCode },
      }).unwrap();
      toast.fire({
        icon: "success",
        title: "Document cloned successfully",
      });
      closeCloneModal();
    } catch (error) {
      console.error("Clone operation failed:", error);
      toast.fire({
        icon: "error",
        title: "Failed to clone the document.",
      });
    }
  };

  const closeCloneModal = () => {
    setIsCloneModalOpen(false);
    setCloneName("");
    setCloneInitiativeCode(null);
    setDocumentToClone(null);
  };

  return (
    <div style={{ paddingTop: isAdmin ? 0 : 26 }}>
      <DataGrid
        dataSource={data && data.master_docs ? data.master_docs : []}
        keyExpr="master_doc_id"
        showBorders
        rowAlternationEnabled={true}
        showRowLines
        onInitNewRow={onInitNewRow}
        onSaving={onSaving}
        allowColumnResizing={true}
        columnWidth="auto"
        columnMinWidth={120}
        onEditorPreparing={(e) => {
          if (e.dataField === "master_doc_type_cd" && !e.row.isNewRow) {
            // Disable editing for the "Type" column if the row is not new
            e.editorOptions.disabled = true;
          }
          // Existing logic for setting popup width, with added admin check
          if (e.parentType === "dataRow" && e.dataField === "initiative_id") {
            // Allow editing if the row is new or if the user is an admin
            e.editorOptions.disabled = !e.row.isNewRow && !isAdmin;
          }

          if (e.parentType === "dataRow") {
            e.editorOptions.onOpened = function (arg) {
              let popupInstance = arg.component._popup;
              popupInstance.option("width", 400);
            };
          }
        }}
        searchPanel={{
          visible: true,
          width: 240,
          placeholder: "Search...",
        }}
        paging={{ enabled: true, pageSize: 30 }}
        pager={{
          showPageSizeSelector: false,
          showInfo: true,
          showNavigationButtons: true,
        }}
      >
        <Editing
          allowAdding={isAllowedRole}
          allowUpdating={isAllowedRole || isExternal}
          allowDeleting={isAllowedRole}
          useIcons
          mode="batch"
        />
        {/* <Paging enabled={false} /> */}
        {/* <Paging
          enabled={true}
          pageSize={20} // Show 20 records per page
        /> */}
        <Column type="buttons">
          <DgButton icon="activefolder" onClick={openMasterDocument} disabled={(e) => e.row.isNewRow} />
          {isAllowedRole && <DgButton hint="Clone" icon="copy" onClick={cloneIconClick} disabled={!isAllowedRole} />}
          <DgButton
            name="delete"
            disabled={({ row }) => {
              return !(row.data.created_by_user_id === user.id || isAdmin);
            }}
          />
        </Column>
        <Column caption="Master Document Name" dataField="master_doc_nm" allowEditing={!isExternal}>
          <RequiredRule />
        </Column>
        <Column caption="Type" dataField="master_doc_type_cd" allowEditing={!isExternal}>
          <Lookup dataSource={docType} displayExpr="type_nm" valueExpr="type_id" />
          <RequiredRule />
        </Column>
        <Column caption="Initiative" dataField="initiative_id" allowEditing={!isExternal}>
          <Lookup dataSource={initiatives} displayExpr="initiative_cd" valueExpr="id" />
          <RequiredRule />
        </Column>
        <Column caption="Status" dataField="status_cd" allowEditing={!isExternal}>
          <Lookup dataSource={statuses} valueExpr="status_cd" displayExpr="status_nm" />
        </Column>
        <Column
          caption="Approved"
          dataField="approved_ind"
          showEditorAlways={false}
          trueText="Yes"
          falseText="No"
          allowEditing={isAdmin}
          editCellRender={boolEditorRender}
        />
        <Column
          caption="External Documentation"
          dataField="external_docs"
          editCellComponent={ExternalDocsEditor}
          // showEditorAlways={false}
          cellRender={externalDocsRender}
        />
        {/* <Column caption="Last Updated By" dataField="last_update_by_user_nm" allowEditing={false} /> */}
        <Column caption="Created By" dataField="created_by_user_nm" allowEditing={false} width="auto" />
        <Column caption="Created On" dataField="create_dt_ut" allowEditing={false} />
        <Column caption="Approved By" dataField="approved_by_user_nm" allowEditing={false} />
        <Column caption="Approved On" dataField="approved_on_dt" allowEditing={false} />
        <Column caption="Last Updated On" dataField="last_update_dt_ut" allowEditing={false} />
      </DataGrid>

      {isCloneModalOpen && (
        <Modal isOpen={true} centered toggle={closeCloneModal}>
          <Form onSubmit={handleCloneSubmit}>
            <Modal.Header toggle={closeCloneModal}>Clone Master Document</Modal.Header>
            <Modal.Body>
              <Form.Group>
                <Label for="snapshot-name" required={true}>
                  <b>Cloning Master Doc:</b> {documentToClone?.master_doc_nm} <br />
                  <b>Name new document:</b>
                  <span style={{ color: "red", marginLeft: 3 }}>*</span>
                </Label>
                <Input type="text" id="snapshot-name" onChange={onCloneNameChange} value={cloneName} required />
                <Label for="snapshot-initiative_cd" required={true}>
                  <br />
                  <b>Select new initiative code:</b>
                  <span style={{ color: "red", marginLeft: 3 }}>*</span>
                </Label>
                <SelectBox
                  onValueChanged={onCloneInitiativeChange}
                  value={cloneInitiativeCode}
                  items={initiatives}
                  valueExpr="id"
                  displayExpr="initiative_cd"
                  deferRendering={false}
                  required
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" type="submit" className="mr-1" disabled={!cloneName || !cloneInitiativeCode}>
                Create
              </Button>
              <Button variant="outline-danger" onClick={closeCloneModal} type="button">
                Cancel
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default MasterDocs;
